import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { usePlayerAdd, usePlayerAddContext } from '../../contexts/PlayerAddContext';
import { RegistrationForm } from '../registration/RegistrationForm';
import { sendRegistrationEmail } from '../../lib/email/registrationEmail';

interface PlayerAddProps {
  tournamentId: string;
  onCancel?: () => void;
}

interface Member {
  id: string;
  name: string;
  email?: string;
  is_senior?: boolean;
  season?: Record<string, {
    status?: string;
    flight?: string;
    handicap?: number;
  }>;
}

export function PlayerAdd({ tournamentId, onCancel }: PlayerAddProps) {
  const { addPlayer, isAdding, error: addError } = usePlayerAdd();
  const { initialPlayers, onPlayersUpdate } = usePlayerAddContext();
  const [name, setName] = useState('');
  const [suggestions, setSuggestions] = useState<Member[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (name.trim().length === 0) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setLoading(true);
      try {
        if (!supabase) {
          throw new Error("Supabase client not initialized");
        }
        
        const { data, error } = await supabase
          .from('members')
          .select('id, name, is_senior, season, email')
          .ilike('name', `%${name.trim()}%`)
          .order('name');

        if (error) throw error;
        
        // Type assertion to ensure data matches the Member interface
        const typedData = (data || []) as Member[];
        setSuggestions(typedData);
        setShowSuggestions(typedData.length > 0);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [name]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setShowRegistrationForm(true);
  };

  const handleSuggestionClick = async (suggestion: Member) => {
    try {
      // Check if player already exists in this tournament
      const playerExists = initialPlayers.some(p => 
        p.tournamentId === tournamentId && 
        (p.id === suggestion.id || p.name.toLowerCase() === suggestion.name.toLowerCase())
      );
      
      if (playerExists) {
        setError(`${suggestion.name} is already registered for this tournament`);
        return;
      }

      await addPlayer(suggestion.id, tournamentId);
      
      // Send registration email
      try {
        console.log('Sending registration email for manual addition:', {
          memberId: suggestion.id,
          memberName: suggestion.name,
          tournamentId
        });
        
        if (!supabase) {
          throw new Error("Supabase client not initialized");
        }
        
        // Simplify tournament retrieval to avoid TypeScript errors
        const { data, error: tournamentError } = await supabase
          .from('tournaments')
          .select('name')
          .eq('id', tournamentId as any)
          .single();
          
        if (tournamentError) {
          throw tournamentError;
        }
        
        // Check if data exists and has a name property
        const tournamentName = data && 'name' in data ? data.name : 'Tournament';

        const emailResult = await sendRegistrationEmail({
          memberId: suggestion.id,
          memberName: suggestion.name,
          tournamentId,
          tournamentName
        });

        console.log('Manual registration email result:', emailResult);
      } catch (emailError) {
        console.error('Failed to send registration email for manual addition:', emailError);
        
        // Skip logging to email_logs to avoid TypeScript errors with table schema
      }

      setName('');
      setSuggestions([]);
      setShowSuggestions(false);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add player');
    }
  };

  const handleRegistration = async (formData: {
    name: string;
    email: string;
    phone?: string;
    join2025: boolean;
    isVietnamese: boolean;
  }) => {
    try {
      // Check if player already exists in this tournament
      const playerExists = initialPlayers.some(p => 
        p.tournamentId === tournamentId && 
        p.name.toLowerCase() === formData.name.toLowerCase()
      );
      
      if (playerExists) {
        setError(`${formData.name} is already registered for this tournament`);
        return;
      }

      if (!supabase) {
        throw new Error("Supabase client not initialized");
      }

      const currentStatus = formData.join2025 && formData.isVietnamese ? 'M' : 
                          formData.join2025 ? 'NVM' : 
                          'inactive';
      
      const membershipStatus = formData.join2025 ? 'active' : 'inactive';

      // Simplified member creation to avoid TypeScript errors
      const memberData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        membership_status: membershipStatus,
        current_status: currentStatus,
        handicap_index: 0.0,
        season: {
          '2025': {
            status: currentStatus,
            flight: 'A',
            handicap: 0.0
          }
        }
      };

      const { data, error: memberError } = await supabase
        .from('members')
        .insert(memberData as any)
        .select()
        .single();

      if (memberError) {
        throw memberError;
      }

      if (data) {
        // Type assertion to access data properties
        const member = data as any;

        // Add player to tournament
        const newPlayer = {
          id: member.id,
          name: member.name,
          tournamentId,
          handicap: member.season?.['2025']?.handicap || 0,
          flight: member.season?.['2025']?.flight || 'A',
          division: member.is_senior ? 'senior' : 'regular',
          scores: [],
          totalScore: 0
        };

        // Simplified player insertion to avoid TypeScript errors
        const playerData = {
          id: newPlayer.id,
          name: newPlayer.name,
          tournament_id: newPlayer.tournamentId,
          handicap: newPlayer.handicap,
          flight: newPlayer.flight,
          division: newPlayer.division,
          scores: newPlayer.scores,
          total_score: newPlayer.totalScore
        };

        const { error: playerError } = await supabase
          .from('players')
          .insert(playerData as any);

        if (playerError) {
          throw playerError;
        }

        // Update local state
        const updatedPlayers = [...initialPlayers, newPlayer];
        onPlayersUpdate(updatedPlayers);

        // Reset form
        setName('');
        setShowRegistrationForm(false);
        setError(null);
      }
    } catch (error) {
      console.error('Failed to register member:', error);
      setError('Failed to register member');
    }
  };

  return (
    <div className="mt-4 p-4 border border-gray-200 rounded-lg">
      <h4 className="text-sm font-medium text-gray-700 mb-4">Add Player</h4>
      {showRegistrationForm ? (
        <RegistrationForm
          onSubmit={handleRegistration}
          onCancel={() => {
            setShowRegistrationForm(false);
            setName('');
          }}
          initialName={name}
        />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setShowSuggestions(true);
                setError(null);
              }}
              onFocus={() => setShowSuggestions(true)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Start typing to search members..."
              autoComplete="off"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              </div>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto z-[60]"
              >
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-primary-50 focus:bg-primary-50 focus:outline-none"
                  >
                    {suggestion.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {(error || addError) && (
            <p className="text-sm text-red-600">{error || addError}</p>
          )}

          <div className="flex justify-end gap-3">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isAdding || !name.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAdding ? 'Adding...' : 'Add Player'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}